import { Observable } from "rxjs";

import st from "lib/utils/index";

import { pushCompanyRoute } from "modules/app/index";
import * as Auth from "modules/auth/index";
import * as DashboardView from "modules/dashboard/index";
import * as CalendarView from "modules/calendar/CalendarView/index";
import * as ShowSlotModal from "modules/calendar/ShowSlotModal/index";
import * as EditProductView from "modules/products/EditProductView/index";
import * as OrdersUtils from "modules/orders/utils";
import * as NewOrderModal from "modules/orders/NewOrderModal/index";
import * as ShowOrderView from "modules/orders/ShowOrderView/index";
import * as OrdersListView from "modules/orders/OrdersListView/index";
import * as FinancesListView from "modules/finances/FinancesListView/index";
import * as CompanyMembersView from "modules/settings/CompanyMembersView/index";
import * as EditCompanyMemberModal from "modules/settings/EditCompanyMemberModal/index";
import * as EditPaymentProfileModal from "modules/settings/EditPaymentProfileModal/index";
import * as PaymentsSettingsView from "modules/settings/PaymentsSettingsView/index";
import * as EditCustomerModal from "modules/customers/EditCustomerModal/index";
import * as ShowCustomerView from "modules/customers/ShowCustomerView/index";
import * as ReviewsListView from "modules/reviews/ReviewsListView/index";
import * as EditReviewModal from "modules/reviews/EditReviewModal/index";
import * as ChangeOrderScheduleSlotModal from "modules/orders/ChangeOrderScheduleSlotModal/index";
import * as EditAgentCooperationView from "modules/agents/EditAgentCooperationView/index";

const epics = [
  // After EditAgentCooperationView removes a cooperation,
  // redirect to AgentsListView
  (actions$, { getState }) =>
    actions$.ofType(EditAgentCooperationView.FETCH_REMOVE_SUCCESS).map(() => {
      const currentCompanyId = getState().auth.company.model.id;
      const coop = EditAgentCooperationView.stateSelector(getState()).form.model;
      const coopCompanyType = coop && currentCompanyId === coop.agent.id ? "supplier" : "agent";
      return pushCompanyRoute(coopCompanyType === "agent" ? "/agents" : "/suppliers");
    }),

  // After ChangeOrderScheduleSlotModal updates an order,
  // - load the order in the ShowOrderView (because propably we opened that modal while being on the order's page)
  (actions$) =>
    actions$
      .ofType(ChangeOrderScheduleSlotModal.FETCH_SUBMIT_SUCCESS)
      .flatMap(({ model: order }) => Observable.of(ShowOrderView.loadOrder(order))),

  // After EditCustomerModal updates a customer,
  // - redirect to ShowCustomerView
  // - load the customer in the ShowCustomerView
  (actions$) =>
    actions$
      .ofType(EditCustomerModal.FETCH_SUBMIT_SUCCESS)
      .flatMap(({ model: customer }) =>
        Observable.of(pushCompanyRoute(`/customers/${customer.id}`), ShowCustomerView.loadCustomer(customer))
      ),

  // After EditCustomerModal removes a customer,
  // redirect to the customers list (because the delete button is visible on the ShowCustomerView)
  (actions$) => actions$.ofType(EditCustomerModal.FETCH_REMOVE_SUCCESS).mapTo(pushCompanyRoute(`/customers`)),

  // After EditReviewModal updates/removes a review,
  // - reload the ReviewsListView records
  (actions$) =>
    actions$
      .ofType(EditReviewModal.FETCH_SUBMIT_SUCCESS)
      .merge(actions$.ofType(EditReviewModal.FETCH_REMOVE_SUCCESS))
      .flatMap(() => Observable.of(ReviewsListView.fetchRecords())),

  // After EditCompanyMemberModal updates/removes a member,
  // refresh all members in CompanyMembersView (because it stores the members data)
  (actions$) =>
    actions$
      .ofType(EditCompanyMemberModal.FETCH_SUBMIT_SUCCESS)
      .merge(actions$.ofType(EditCompanyMemberModal.FETCH_REMOVE_SUCCESS))
      .mapTo(CompanyMembersView.fetchMembers()),

  // After EditCompanyMemberModal updates/removes a member,
  // refresh all members in CompanyMembersView (because it stores the members data)
  (actions$) =>
    actions$
      .ofType(EditProductView.FETCH_SAVE_SUCCESS)
      .merge(actions$.ofType(EditProductView.FETCH_REMOVE_SUCCESS))
      .mapTo(Auth.fetchProducts()),

  // After NewOrderModal creates an order,
  // load it in ShowOrderView (because we might have created that order while being in the ShowOrderView)
  (actions$, { getState }) =>
    actions$.ofType(NewOrderModal.FETCH_SUBMIT_SUCCESS).flatMap(({ model: order }) => {
      const { orderId: currentOrderId, orderToken: currentOrderToken } = ShowOrderView.stateSelector(getState());
      console.log(order && order.id === currentOrderId);

      return order && order.id === currentOrderId ? Observable.of(ShowOrderView.loadOrder(order)) : Observable.empty();
    }),

  // After callOrderAction gets resolved,
  // update the order in OrdersListView (because we might have approved/rejected that order in OrdersListView)
  (actions$, { getState }) =>
    actions$.ofType(OrdersUtils.CALL_ORDER_ACTION_SUCCESS).flatMap(({ model: order, params: { actionName } }) => {
      const {
        records: { model },
      } = OrdersListView.stateSelector(getState());
      if (model && model.records.find((o) => o.id === order.id)) {
        const nextRecords =
          actionName === "delete"
            ? model.records.filter((o) => o.id !== order.id)
            : model.records.map((o) => (o.id === order.id ? order : o));
        return Observable.of(
          OrdersListView.loadRecords({
            ...model,
            records: nextRecords,
          })
        );
      } else {
        return Observable.empty();
      }
    }),

  // After callBulkOrdersAction gets resolved,
  // refresh the FinancesListView
  (actions$, { getState }) =>
    actions$.ofType(OrdersUtils.CALL_BULK_ORDERS_ACTION_SUCCESS).mapTo(FinancesListView.fetchRecords()),

  // After callOrderAction delete action gets resolved,
  // redirect to /orders (because delete button is visible on the ShowOrderView)
  (actions$, { getState }) =>
    actions$
      .ofType(OrdersUtils.CALL_ORDER_ACTION_SUCCESS)
      .filter(({ params: { actionName } }) => actionName === "delete")
      .mapTo(pushCompanyRoute("/orders")),

  // After callOrderAction gets resolved,
  // load it in ShowSlotModal (because we might have approved/rejected that order in ShowSlotModal)
  (actions$, { getState }) =>
    actions$.ofType(OrdersUtils.CALL_ORDER_ACTION_SUCCESS).flatMap(({ model: order }) => {
      const {
        opened,
        slot: { model: slot },
      } = ShowSlotModal.stateSelector(getState());
      return opened && (order && order.product_schedule_slot && order.product_schedule_slot.id) === (slot && slot.id)
        ? Observable.of(ShowSlotModal.fetchSlot())
        : Observable.empty();
    }),

  // After callOrderAction gets resolved,
  // refresh ShowOrderModal (because we might have issued this action in ShowOrderView)
  (actions$, { getState }) =>
    actions$.ofType(OrdersUtils.CALL_ORDER_ACTION_SUCCESS).flatMap(({ model: order }) => {
      const { orderId: currentOrderId, orderToken } = ShowOrderView.stateSelector(getState());

      return order && order.id === currentOrderId ? Observable.of(ShowOrderView.loadOrder(order)) : Observable.empty();
    }),

  // After callOrderAction gets resolved,
  // refresh DashboardView (because we might have approved/rejected that order in DashboardView)
  (actions$, { getState }) =>
    actions$.ofType(OrdersUtils.CALL_ORDER_ACTION_SUCCESS).flatMap(({ model: order }) => {
      return Observable.of(DashboardView.fetchPendingOrders());
    }),

  // After a payment profile gets updated/removed,
  // update the current user's data (because AdminUserFullSerializer stores payment profiles data)
  (actions$) =>
    actions$
      .ofType(EditPaymentProfileModal.FETCH_SUBMIT_SUCCESS)
      .merge(actions$.ofType(EditPaymentProfileModal.FETCH_REMOVE_SUCCESS))
      .mapTo(Auth.fetchCurrentCompany()),

  // After the current company is fetched,
  // update the payment profiles data in the current view
  (actions$, { getState }) =>
    actions$.ofType(Auth.FETCH_CURRENT_COMPANY_SUCCESS).flatMap(() => {
      return Observable.of(PaymentsSettingsView.reloadPaymentProfiles(), EditProductView.reloadPaymentProfiles());
    }),

  // After ShowSlotModal submits a slot,
  // - refresh CalendarView
  (actions$, { getState }) =>
    actions$.ofType(ShowSlotModal.FETCH_SUBMIT_SUCCESS).flatMap(({ model: slot }) => {
      const {
        slots: { model: slots },
      } = CalendarView.stateSelector(getState());
      return slots && slots.find((sl) => sl.id === slot.id)
        ? Observable.of(CalendarView.loadSlots(slots.map((sl) => (sl.id === slot.id ? slot : sl))))
        : Observable.empty();
    }),

  // After ShowSlotModal submits a slot,
  // - refresh ShowOrderView
  (actions$, { getState }) =>
    actions$.ofType(ShowSlotModal.FETCH_SUBMIT_SUCCESS).flatMap(({ model: slot }) => {
      const {
        order: { model: order },
      } = ShowOrderView.stateSelector(getState());
      return order && order.product_schedule_slot && order.product_schedule_slot.id === slot.id
        ? Observable.of(ShowOrderView.fetchOrder())
        : Observable.empty();
    }),
];

export default st.duck.createDuck({}, {}, epics);



// WEBPACK FOOTER //
// ./src/modules/synchronization/index.js